import { useState, type KeyboardEvent, type ReactNode } from "react";

import { cx } from "../lib/cx";
import { ShortcutsGuide } from "../shortcuts/ShortcutsGuide";
import { Dialog } from "../ui/Dialog";
import { EditingGuide } from "./EditingGuide";

export type HelpTab = "editing" | "shortcuts";

const TABS: { id: HelpTab; label: string }[] = [
  { id: "editing", label: "Editing" },
  { id: "shortcuts", label: "Keyboard shortcuts" },
];

/**
 * The one help surface: the header's "?" button opens it on the editing guide, the `?` key
 * and the `?shortcuts` link open it on the shortcut table. Two tabs, arrow keys between them.
 */
export function HelpDialog({
  tab,
  onClose,
}: {
  /** Which tab to open on; null keeps the dialog closed. */
  tab: HelpTab | null;
  onClose: () => void;
}): ReactNode {
  const [active, setActive] = useState<HelpTab>(tab ?? "editing");
  // Re-open on the requested tab: derived during render, the way React documents for
  // "reset some state when a prop changes", rather than one render late from an effect.
  const [requested, setRequested] = useState(tab);
  if (tab !== requested) {
    setRequested(tab);
    if (tab !== null) setActive(tab);
  }

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const index = TABS.findIndex((entry) => entry.id === active);
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = TABS[(index + step + TABS.length) % TABS.length];
    if (next) {
      setActive(next.id);
      document.getElementById(`help-tab-${next.id}`)?.focus();
    }
  };

  return (
    <Dialog open={tab !== null} onClose={onClose} title="Help" width="lg">
      <div role="tablist" aria-label="Help topics" className="mb-4 flex gap-1 border-b border-line">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            id={`help-tab-${entry.id}`}
            aria-selected={active === entry.id}
            aria-controls={`help-panel-${entry.id}`}
            tabIndex={active === entry.id ? 0 : -1}
            onClick={() => {
              setActive(entry.id);
            }}
            onKeyDown={onTabKeyDown}
            className={cx(
              "-mb-px border-b-2 px-3 py-1.5 text-sm",
              active === entry.id
                ? "border-accent font-medium text-ink"
                : "border-transparent text-muted hover:text-ink",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {TABS.map((entry) => (
        <div
          key={entry.id}
          role="tabpanel"
          id={`help-panel-${entry.id}`}
          aria-labelledby={`help-tab-${entry.id}`}
          hidden={active !== entry.id}
        >
          {active === entry.id && (entry.id === "editing" ? <EditingGuide /> : <ShortcutsGuide />)}
        </div>
      ))}
    </Dialog>
  );
}
